import { useEffect, useRef, useState } from 'react';
import {
  addComment,
  approveLlmComment,
  deleteLlmComment,
  deleteTaskComment,
  editLlmComment,
  editOwnComment,
  markCommentAddressed,
  subscribeToComments,
} from '../../services/taskComments';
import {
  AddCommentCommand,
  CommentsController,
  CommentsControllerAction,
  CommentsControllerPorts,
} from './commentsController';
import { TaskSessionActor } from './taskSessionController';

export interface UseTaskCommentsControllerOptions {
  taskId: string | undefined;
  taskTitle: string;
  reviewRound: number;
  actor: TaskSessionActor | null;
  reportError?: (error: Error, action: CommentsControllerAction, taskId: string) => void;
}

function asFirebaseUser(actor: TaskSessionActor): Parameters<typeof addComment>[0]['user'] {
  return {
    uid: actor.uid,
    email: actor.email ?? null,
    displayName: actor.displayName ?? null,
  } as Parameters<typeof addComment>[0]['user'];
}

const defaultPorts: CommentsControllerPorts = {
  subscribe: subscribeToComments,
  addComment: (command: AddCommentCommand) => addComment({
    taskId: command.taskId,
    taskTitle: command.taskTitle,
    kind: command.kind,
    text: command.text,
    user: asFirebaseUser(command.actor),
    reviewRound: command.reviewRound,
    fieldPath: command.fieldPath,
    fieldAnchor: command.fieldAnchor,
    parentCommentId: command.parentCommentId,
  }),
  editOwnComment: (commentId, actor, text) => editOwnComment(commentId, asFirebaseUser(actor), text),
  deleteComment: deleteTaskComment,
  markAddressed: (commentId, actor, addressed) => (
    markCommentAddressed(commentId, asFirebaseUser(actor), addressed)
  ),
  approveLlmComment: (commentId, actor) => approveLlmComment(commentId, asFirebaseUser(actor)),
  editLlmComment: (commentId, actor, text) => editLlmComment(commentId, asFirebaseUser(actor), text),
  deleteLlmComment,
};

export function useTaskCommentsController(options: UseTaskCommentsControllerOptions) {
  const runtimeRef = useRef<Pick<UseTaskCommentsControllerOptions, 'reportError'>>({
    reportError: options.reportError,
  });
  runtimeRef.current = { reportError: options.reportError };

  const portsRef = useRef<CommentsControllerPorts | null>(null);
  if (!portsRef.current) {
    portsRef.current = {
      ...defaultPorts,
      reportError: (error, action, taskId) => runtimeRef.current.reportError?.(error, action, taskId),
    };
  }
  const controllerRef = useRef<CommentsController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new CommentsController(portsRef.current);
  }
  const controller = controllerRef.current;
  const [, setVersion] = useState(0);

  useEffect(() => controller.subscribe(() => setVersion((version) => version + 1)), [controller]);

  useEffect(() => {
    controller.updateContext({
      taskId: options.taskId,
      taskTitle: options.taskTitle,
      reviewRound: options.reviewRound,
      actor: options.actor,
    });
  }, [controller, options.actor, options.reviewRound, options.taskId, options.taskTitle]);

  useEffect(() => () => controller.dispose(), [controller]);

  return {
    controller,
    ...controller.getSnapshot(),
    initialize: () => controller.initialize(),
    dispose: () => controller.dispose(),
    addFieldComment: (
      fieldPath: string,
      text: string,
      fieldAnchor?: AddCommentCommand['fieldAnchor'],
    ) => controller.addFieldComment(fieldPath, text, fieldAnchor),
    addReply: (rootCommentId: string, text: string) => controller.addReply(rootCommentId, text),
    editComment: (commentId: string, text: string) => controller.editComment(commentId, text),
    deleteComment: (commentId: string) => controller.deleteComment(commentId),
    markAddressed: (commentId: string, addressed: boolean) => (
      controller.markAddressed(commentId, addressed)
    ),
    approveLlmComment: (commentId: string) => controller.approveLlmComment(commentId),
    editLlmComment: (commentId: string, text: string) => controller.editLlmComment(commentId, text),
    deleteLlmComment: (commentId: string) => controller.deleteLlmComment(commentId),
    clearError: () => controller.clearError(),
  };
}
